import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import Button from "@/components/ui/button";
import { useWidgets } from "../hooks/use-widgets";
import { getFallbackPreview } from "../constants/widget-templates";
import type { WidgetTemplateListItem } from "../types";
import { useBrands, usePlan } from "@/modules/brand";
import { useOpenUpgradeModal } from "@/hooks/use-upgrade-modal";

interface WidgetTemplateModalProps {
	open: boolean;
	currentTemplate: string | null;
	currentVersion: number | null;
	onOpenChange: (open: boolean) => void;
	onConfirm: (template: string, version: number) => void;
}

function TemplatePreviewImage({ template }: { template: WidgetTemplateListItem }) {
	const fallbackPreview = getFallbackPreview(template.template);

	if (template.preview_image_url) {
		return (
			<div className="h-full w-full bg-gray-50 overflow-hidden">
				<img src={template.preview_image_url} alt={template.label} className="w-full h-full object-contain" />
			</div>
		);
	}

	if (fallbackPreview) {
		return <div className="h-full w-full overflow-hidden">{fallbackPreview}</div>;
	}

	return (
		<div className="h-full w-full bg-gray-50 flex items-center justify-center">
			<span className="text-xs text-gray-400">Preview</span>
		</div>
	);
}

function TemplateItem({ onClick, isSelected, template, supportedWidgetLevel, onUpgradeClick }: { onClick: () => void; isSelected: boolean; template: WidgetTemplateListItem; supportedWidgetLevel: number; onUpgradeClick: () => void }) {
	const isPremium = template.level > supportedWidgetLevel;
	const buttonClasses = isSelected ? "border-brand ring-2 ring-brand/20" : "border-gray-200 hover:border-gray-300";

	if (isPremium) {
		return (
			<button
				type="button"
				onClick={onUpgradeClick}
				className="group flex flex-col h-44 text-left rounded-xl border-2 overflow-hidden transition-all border-gray-200 hover:border-gray-300"
			>
				<div className="relative flex-1 min-h-0">
					<TemplatePreviewImage template={template} />
					<span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-medium bg-white/90 text-gray-700 rounded-full shadow-sm capitalize">{template.display_type}</span>

					<span className="absolute top-2 right-2 inline-block p-1 text-blue-600 rounded-lg bg-blue-50 border border-blue-200">
						<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
							<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
						</svg>
					</span>
				</div>
				<div className="px-4 py-3 shrink-0">
					<h4 className="text-sm font-semibold text-gray-900">{template.label}</h4>
					<p className="text-xs text-tertiary line-clamp-2 max-h-0 group-hover:max-h-8 overflow-hidden transition-[max-height] duration-200">{template.description}</p>
				</div>
			</button>
		);
	}

	return (
		<button type="button" onClick={onClick} className={`group flex flex-col h-44 text-left rounded-xl border-2 overflow-hidden transition-all ${buttonClasses}`}>
			<div className="relative flex-1 min-h-0">
				<TemplatePreviewImage template={template} />
				<span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-medium bg-white/90 text-gray-700 rounded-full shadow-sm capitalize">{template.display_type}</span>
			</div>
			<div className="px-4 py-3 shrink-0">
				<h4 className="text-sm font-semibold text-gray-900">{template.label}</h4>
				<p className="text-xs text-tertiary line-clamp-2 max-h-0 group-hover:max-h-8 overflow-hidden transition-[max-height] duration-200">{template.description}</p>
			</div>
		</button>
	);
}

export default function WidgetTemplateModal({ open, currentTemplate, currentVersion, onOpenChange, onConfirm }: WidgetTemplateModalProps) {
	const { templates, listTemplates } = useWidgets();
	const [selectedKey, setSelectedKey] = useState<string | null>(currentTemplate && currentVersion ? `${currentTemplate}:${currentVersion}` : null);
	const [isLoading, setIsLoading] = useState(false);
	const { planDetails } = usePlan();
	const { selectedBrand } = useBrands();
	const openUpgradeModal = useOpenUpgradeModal();

	const supportedWidgetLevel = planDetails?.limits?.supported_widget_level ?? 0;

	useEffect(() => {
		if (open) {
			setSelectedKey(currentTemplate && currentVersion ? `${currentTemplate}:${currentVersion}` : null);

			if (!templates) {
				setIsLoading(true);
				listTemplates().finally(() => setIsLoading(false));
			}
		}
	}, [open, currentTemplate, currentVersion, templates, listTemplates]);

	const handleConfirm = () => {
		if (selectedKey) {
			const [template, version] = selectedKey.split(":");
			onConfirm(template, Number(version));
		}
		onOpenChange(false);
	};

	const handleCancel = () => {
		setSelectedKey(currentTemplate && currentVersion ? `${currentTemplate}:${currentVersion}` : null);
		onOpenChange(false);
	};

	const handleUpgradeClick = () => {
		onOpenChange(false);
		openUpgradeModal({
			context: "premium_widget",
			userRole: selectedBrand?.role,
			onMaybeLater: () => {
				onOpenChange(true);
			},
		});
	};

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 modal-overlay" />
				<Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-4xl modal-content overflow-hidden flex flex-col max-h-[90vh]">
					<div className="px-6 pt-6 pb-4 border-b border-gray-200">
						<Dialog.Title className="text-base font-semibold text-gray-900 leading-base">Select Widget Template</Dialog.Title>
						<Dialog.Description className="text-sm text-gray-500 leading-sm mt-1">Choose a different template for your widget.</Dialog.Description>
					</div>

					<div className="overflow-y-auto px-6 py-4">
						{isLoading ? (
							<div className="grid grid-cols-3 gap-4">
								{Array.from({ length: 3 }).map((_, i) => (
									<div key={i} className="rounded-xl border-2 border-gray-200 p-3 animate-pulse">
										<div className="h-20 w-full rounded-lg bg-gray-100 mb-2" />
										<div className="h-3 w-16 bg-gray-100 rounded mb-1" />
										<div className="h-2.5 w-24 bg-gray-100 rounded" />
									</div>
								))}
							</div>
						) : (
							<div className="grid grid-cols-3 gap-4 items-start">
								{(templates || []).map((template) => {
									const key = `${template.template}:${template.version}`;
									const isSelected = selectedKey === key;

									return (
										<TemplateItem key={key} isSelected={isSelected} template={template} supportedWidgetLevel={supportedWidgetLevel} onClick={() => setSelectedKey(key)} onUpgradeClick={handleUpgradeClick} />
										// <button
										// 	key={key}
										// 	type="button"
										// 	onClick={() => setSelectedKey(key)}
										// 	className={`group flex flex-col h-44 text-left rounded-xl border-2 overflow-hidden transition-all ${isSelected ? "border-brand ring-2 ring-brand/20" : "border-gray-200 hover:border-gray-300"}`}
										// >
										// 	<div className="relative flex-1 min-h-0">
										// 		<TemplatePreviewImage template={template} />
										// 		<span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-medium bg-white/90 text-gray-700 rounded-full shadow-sm capitalize">
										// 			{template.display_type}
										// 		</span>
										// 	</div>
										// 	<div className="px-4 py-3 shrink-0">
										// 		<h4 className="text-sm font-semibold text-gray-900">{template.label}</h4>
										// 		<p className="text-xs text-tertiary line-clamp-2 max-h-0 group-hover:max-h-8 overflow-hidden transition-[max-height] duration-200">
										// 			{template.description}
										// 		</p>
										// 	</div>
										// </button>
									);
								})}
							</div>
						)}
					</div>

					<div className="mx-6 mb-2 rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-600 flex items-center justify-between gap-4">
						<div className="max-w-md">
							<p className="text-sm font-medium text-gray-900">Looking for something different?</p>
							<p className="text-xs text-gray-500 mt-0.5">Send us a screenshot of the widget you have in mind, and we'll look into it for you.</p>
						</div>
						<a
							href="https://sayaboutus.featurebase.app/"
							target="_blank"
							rel="noopener noreferrer"
							className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors"
						>
							Request a Widget
							<svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
							</svg>
						</a>
					</div>

					<div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
						<Button type="button" variant="soft" onClick={handleCancel}>
							Cancel
						</Button>
						<Button type="button" variant="brand" onClick={handleConfirm} disabled={!selectedKey}>
							Confirm
						</Button>
					</div>

					<Dialog.Close asChild>
						<button className="absolute top-7 right-6 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
							<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</Dialog.Close>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
